/**
 * CRM team chat — STOMP/SockJS for live timeline; REST only for initial history, send, and read receipts.
 *
 * - SockJS: `/websocket/app?access_token=…` + STOMP CONNECT `Authorization: Bearer …`
 * - Subscribe: `/topic/chat.rooms.{roomId}` → `{ eventType, message }` (CREATED | UPDATED | DELETED)
 * - Subscribe: `/topic/chat.user.{username}` → `MEMBER_ADDED` when added to a room
 * - Typing: `/topic/chat.rooms.{roomId}.typing` / publish `/app/chat.rooms.{roomId}.typing`
 * - REST: GET history once per room, POST send (backend broadcasts CREATED), PATCH read
 */
/** STOMP publish body for `/app/chat.rooms.{roomId}.typing`. */
export function buildChatTypingPayload(userId, typing) {
    return JSON.stringify({ userId, typing });
}
/** Parse `/topic/chat.rooms.{roomId}.typing` frame body (`{ userId, typing }`). */
export function parseChatTypingEvent(raw) {
    try {
        const parsed = JSON.parse(raw);
        const userIdRaw = [parsed.userId, parsed.senderId, parsed.username, parsed.user].find((v) => typeof v === 'string' && v.trim());
        const userId = typeof userIdRaw === 'string' ? userIdRaw.trim() : '';
        if (!userId)
            return null;
        const typing = parsed.typing !== false && parsed.typing !== 'false' && parsed.typing !== 0;
        return { userId, typing };
    }
    catch {
        return null;
    }
}
export const chatInboundMessageDefault = {
    fromUser: '',
    content: '',
    timestamp: null,
};
/** STOMP topic for room message events */
export function buildChatRoomTopic(roomId) {
    return `/topic/chat.rooms.${roomId}`;
}
/** STOMP topic for typing indicators */
export function buildChatTypingTopic(roomId) {
    return `/topic/chat.rooms.${roomId}.typing`;
}
/** Publish destination for typing state */
export function buildChatTypingDestination(roomId) {
    return `/app/chat.rooms.${roomId}.typing`;
}
/** Normalize REST/STOMP sender fields (`senderId`, `sender`, `fromUser`, …). */
function readChatSenderId(dto) {
    const candidates = [
        dto.senderId,
        dto['sender'],
        dto['fromUser'],
        dto['from'],
        dto['username'],
    ];
    for (const c of candidates) {
        const s = typeof c === 'string' ? c.trim() : '';
        if (s)
            return s;
    }
    return '';
}
/** Oldest → newest (stable tie-break on `id`). Backend may ignore `sort=createdAt,asc`. */
export function sortChatMessageDtos(dtos) {
    return [...dtos].sort((a, b) => {
        const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        if (ta !== tb)
            return ta - tb;
        return (a.id ?? 0) - (b.id ?? 0);
    });
}
export function newestChatMessageDto(dtos) {
    const sorted = sortChatMessageDtos(dtos);
    return sorted.length > 0 ? sorted[sorted.length - 1] : undefined;
}
export function chatMessageDtoToInbound(dto) {
    const content = dto.content ?? '';
    const sender = readChatSenderId(dto);
    if (!sender && !content && dto.deletedAt == null) {
        return null;
    }
    return {
        id: dto.id,
        fromUser: sender || 'unknown',
        content: dto.deletedAt ? '(message deleted)' : content,
        timestamp: dto.createdAt ?? dto.editedAt ?? null,
        deleted: Boolean(dto.deletedAt),
    };
}
function normalizeLiveEventType(raw) {
    const t = (raw ?? '').toUpperCase();
    if (t === 'UPDATED' || t === 'EDITED' || t === 'UPDATE')
        return 'UPDATED';
    if (t === 'DELETED' || t === 'DELETE' || t === 'REMOVED')
        return 'DELETED';
    if (t === 'CREATED' || t === 'CREATE' || t === 'NEW' || t === 'MESSAGE' || t === 'SENT') {
        return 'CREATED';
    }
    return 'CREATED';
}
export function parseChatLiveEvent(raw) {
    try {
        let parsed = JSON.parse(raw);
        if (typeof parsed === 'string') {
            parsed = JSON.parse(parsed);
        }
        const eventType = (parsed.eventType ?? parsed.type);
        const nested = parsed.message ?? parsed['payload'] ?? parsed['data'];
        const dto = nested && typeof nested === 'object'
            ? nested
            : parsed.id != null ||
                parsed.senderId != null ||
                parsed.content != null ||
                parsed['sender'] != null
                ? parsed
                : null;
        if (!dto)
            return null;
        const inbound = chatMessageDtoToInbound(dto);
        if (!inbound)
            return null;
        return { type: normalizeLiveEventType(eventType), message: inbound };
    }
    catch {
        return null;
    }
}
/** Merge REST history with live STOMP entries (dedupe by message id, chronological). */
export function mergeChatTimelineEntries(existing, fromHistory) {
    const byId = new Map();
    for (const e of fromHistory) {
        if (e.type === 'message' && e.message.id != null) {
            byId.set(e.message.id, e);
        }
    }
    for (const e of existing) {
        if (e.type === 'message' && e.message.id != null) {
            byId.set(e.message.id, e);
        }
    }
    const messages = [...byId.values()].sort((a, b) => {
        if (a.type !== 'message' || b.type !== 'message')
            return 0;
        const ta = a.message.timestamp ? new Date(a.message.timestamp).getTime() : 0;
        const tb = b.message.timestamp ? new Date(b.message.timestamp).getTime() : 0;
        if (ta !== tb)
            return ta - tb;
        return (a.message.id ?? 0) - (b.message.id ?? 0);
    });
    const systems = fromHistory.filter((e) => e.type === 'system');
    return systems.length > 0 ? [...systems, ...messages] : messages;
}
export function formatChatTime(timestamp) {
    if (!timestamp) {
        return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    const d = new Date(timestamp);
    return Number.isNaN(d.getTime())
        ? new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
